// Trailing Stop-Loss Persistence Service
// Handles saving and loading TSL configurations from localStorage

import type { TrailingStopLossConfig, TrailingStopLossState } from '../types/mexc';

const TSL_STORAGE_KEY = 'mexc_trailing_stop_loss_configs';
const TSL_GLOBAL_SETTINGS_KEY = 'mexc_tsl_global_settings';

// Default TSL settings
const DEFAULT_TSL_SETTINGS = {
  trailingType: 'percentage' as const,
  trailingDistance: 0.5, // 0.5%
  minProfitThreshold: 0.1, // 0.1% minimum profit before trailing begins
};

export class TrailingStopLossPersistenceService {
  /**
   * Save a TSL configuration for a specific position
   */
  static saveTSLConfig(config: TrailingStopLossConfig): void {
    try {
      const existingConfigs = this.getAllTSLConfigs();
      existingConfigs[config.positionId] = {
        ...config,
        updatedAt: Date.now(),
      };
      
      localStorage.setItem(TSL_STORAGE_KEY, JSON.stringify(existingConfigs));
      console.log('TSL config saved for position:', config.positionId);
    } catch (error) {
      console.error('Failed to save TSL config:', error);
      throw new Error('Failed to save trailing stop-loss configuration');
    }
  }

  /**
   * Get TSL configuration for a specific position
   */
  static getTSLConfig(positionId: number): TrailingStopLossConfig | null {
    try {
      const configs = this.getAllTSLConfigs();
      return configs[positionId] || null;
    } catch (error) {
      console.error('Failed to get TSL config:', error);
      return null;
    }
  }

  /**
   * Get all TSL configurations
   */
  static getAllTSLConfigs(): Record<number, TrailingStopLossConfig> {
    try {
      const stored = localStorage.getItem(TSL_STORAGE_KEY);
      if (!stored) return {};
      
      const configs = JSON.parse(stored);
      
      // Validate and clean up old/invalid configs
      const validConfigs: Record<number, TrailingStopLossConfig> = {};
      const now = Date.now();
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
      
      Object.entries(configs).forEach(([positionId, config]) => {
        const tslConfig = config as TrailingStopLossConfig;
        
        // Remove configs older than 30 days or invalid
        if (
          tslConfig.createdAt &&
          (now - tslConfig.createdAt) < maxAge &&
          this.validateTSLConfig(tslConfig)
        ) {
          validConfigs[Number(positionId)] = tslConfig;
        }
      });
      
      return validConfigs;
    } catch (error) {
      console.error('Failed to load TSL configs:', error);
      return {};
    }
  }

  /**
   * Remove TSL configuration for a specific position
   */
  static removeTSLConfig(positionId: number): void {
    try {
      const configs = this.getAllTSLConfigs();
      delete configs[positionId];
      localStorage.setItem(TSL_STORAGE_KEY, JSON.stringify(configs));
      console.log('TSL config removed for position:', positionId);
    } catch (error) {
      console.error('Failed to remove TSL config:', error);
    }
  }

  /**
   * Clear all TSL configurations
   */
  static clearAllTSLConfigs(): void {
    try {
      localStorage.removeItem(TSL_STORAGE_KEY);
      console.log('All TSL configs cleared');
    } catch (error) {
      console.error('Failed to clear TSL configs:', error);
    }
  }

  /**
   * Save global TSL settings
   */
  static saveGlobalSettings(settings: Partial<TrailingStopLossState['defaultSettings']>): void {
    try {
      const currentSettings = this.getGlobalSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      localStorage.setItem(TSL_GLOBAL_SETTINGS_KEY, JSON.stringify(updatedSettings));
      console.log('Global TSL settings saved:', updatedSettings);
    } catch (error) {
      console.error('Failed to save global TSL settings:', error);
    }
  }

  /**
   * Get global TSL settings
   */
  static getGlobalSettings(): TrailingStopLossState['defaultSettings'] {
    try {
      const stored = localStorage.getItem(TSL_GLOBAL_SETTINGS_KEY);
      if (!stored) return DEFAULT_TSL_SETTINGS;
      
      const settings = JSON.parse(stored);
      return { ...DEFAULT_TSL_SETTINGS, ...settings };
    } catch (error) {
      console.error('Failed to load global TSL settings:', error);
      return DEFAULT_TSL_SETTINGS;
    }
  }

  /**
   * Get complete TSL state
   */
  static getTSLState(): TrailingStopLossState {
    return {
      configs: this.getAllTSLConfigs(),
      globalEnabled: this.getGlobalEnabled(),
      defaultSettings: this.getGlobalSettings(),
    };
  }

  /**
   * Save global enabled state
   */
  static setGlobalEnabled(enabled: boolean): void {
    try {
      localStorage.setItem('mexc_tsl_global_enabled', JSON.stringify(enabled));
      console.log('Global TSL enabled state set to:', enabled);
    } catch (error) {
      console.error('Failed to save global TSL enabled state:', error);
    }
  }

  /**
   * Get global enabled state
   */
  static getGlobalEnabled(): boolean {
    try {
      const stored = localStorage.getItem('mexc_tsl_global_enabled');
      return stored ? JSON.parse(stored) : true; // Default to enabled
    } catch (error) {
      console.error('Failed to load global TSL enabled state:', error);
      return true;
    }
  }

  /**
   * Validate TSL configuration
   */
  private static validateTSLConfig(config: any): config is TrailingStopLossConfig {
    return (
      config &&
      typeof config.positionId === 'number' &&
      typeof config.symbol === 'string' &&
      typeof config.enabled === 'boolean' &&
      typeof config.trailingDistance === 'number' &&
      config.trailingDistance > 0 &&
      ['percentage', 'fixed'].includes(config.trailingType) &&
      typeof config.isActive === 'boolean' &&
      typeof config.createdAt === 'number'
    );
  }

  /**
   * Export TSL configurations for backup
   */
  static exportConfigs(): string {
    const state = this.getTSLState();
    return JSON.stringify(state, null, 2);
  }

  /**
   * Import TSL configurations from backup
   */
  static importConfigs(jsonData: string): boolean {
    try {
      const state = JSON.parse(jsonData) as TrailingStopLossState;
      
      // Validate imported data
      if (!state.configs || !state.defaultSettings) {
        throw new Error('Invalid TSL configuration format');
      }
      
      // Save imported configurations
      localStorage.setItem(TSL_STORAGE_KEY, JSON.stringify(state.configs));
      localStorage.setItem(TSL_GLOBAL_SETTINGS_KEY, JSON.stringify(state.defaultSettings));
      
      if (typeof state.globalEnabled === 'boolean') {
        this.setGlobalEnabled(state.globalEnabled);
      }
      
      console.log('TSL configurations imported successfully');
      return true;
    } catch (error) {
      console.error('Failed to import TSL configurations:', error);
      return false;
    }
  }

  /**
   * Get statistics about stored TSL configurations
   */
  static getStats(): {
    totalConfigs: number;
    activeConfigs: number;
    enabledConfigs: number;
    oldestConfig: number | null;
    newestConfig: number | null;
  } {
    const configs = this.getAllTSLConfigs();
    const configArray = Object.values(configs);
    
    return {
      totalConfigs: configArray.length,
      activeConfigs: configArray.filter(c => c.isActive).length,
      enabledConfigs: configArray.filter(c => c.enabled).length,
      oldestConfig: configArray.length > 0 ? Math.min(...configArray.map(c => c.createdAt)) : null,
      newestConfig: configArray.length > 0 ? Math.max(...configArray.map(c => c.createdAt)) : null,
    };
  }
}
